import { useCallback, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useThemeMode } from '@/contexts/ThemeContext'
import { useUser } from '@/hooks/useUser'
import { api } from '@/lib/api/client'
import { normalizeEmojis } from '@/lib/emojiUtils'
import { parseRepresentationMarkdown } from '@/lib/honcho/parseRepresentation'
import { theme } from '@/lib/theme'
import { useSessionContext } from '@/contexts/chat/SessionContext'
import WorkingRepresentationAsciiViz from './WorkingRepresentationAsciiViz'
import { useRepresentationStore } from '@/store/representationStore'
import { RepresentationHelpProvider, RepresentationHelpToggle, useRepresentationHelp } from './helpers/RepresentationHelp'
import useIsMobile from '@/hooks/useIsMobile'
import { LoadingText, loadingPresets } from '@/lib/loadingAnimations'

interface RepresentationContentProps {
  isActive?: boolean
}

/**
 * Representation component that displays the user's working representation as a word cloud
 */
function RepresentationContent({ isActive = true }: RepresentationContentProps) {
  const { t, i18n } = useTranslation()
  const { helpEnabled } = useRepresentationHelp()
  const { colors } = useThemeMode()
  const isMobile = useIsMobile()
  const { user, isLoading: isUserLoading } = useUser()
  const [workingRepresentation, setWorkingRepresentation] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchText, setSearchText] = useState('')
  const [isSearchLoading, setIsSearchLoading] = useState(false)
  const [isSearchMode, setIsSearchMode] = useState(false)
  const [currentSearchQuery, setCurrentSearchQuery] = useState('')
  const { currentSession } = useSessionContext()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 })
  const { clearVizState } = useRepresentationStore()

  const fetchWorkingRepresentation = useCallback(async (showLoading = false) => {
    try {
      // Only show loading screen on initial load, not on updates
      if (showLoading) {
        setIsLoading(true)
      }
      setError(null)

      const { data, error } = await api.api.profile['working-representation'].get()

      if (error) throw new Error('Failed to fetch working representation')

      // Honcho v3 SDK returns a markdown string, parse it into structured format
      const rawRepresentation = data.workingRepresentation
      const parsedRepresentation = typeof rawRepresentation === 'string'
        ? parseRepresentationMarkdown(rawRepresentation)
        : rawRepresentation

      setWorkingRepresentation(parsedRepresentation)
    } catch (err) {
      console.error('Error fetching working representation:', err)
      setError('Failed to load working representation')
    } finally {
      if (showLoading) {
        setIsLoading(false)
      }
    }
  }, [])

  const fetchContextualRepresentation = useCallback(async (inputText: string) => {
    if (!inputText.trim()) {
      return
    }

    try {
      setIsSearchLoading(true)
      setError(null)

      // Clear visualization state before loading contextual representation
      clearVizState()

      const { data, error } = await api.api.chat['get-context'].post({
          sessionId: currentSession.id,
          targeting: {
            lastUserMessage: inputText.trim()
          }
        })

      if (error) throw new Error('Failed to fetch contextual representation')

      // Extract peer representation from the context
      // Honcho v3 returns a markdown string, parse it into structured format
      const peerRepresentation = data.context?.peerRepresentation
      if (peerRepresentation) {
        const parsedRepresentation = typeof peerRepresentation === 'string'
          ? parseRepresentationMarkdown(peerRepresentation)
          : { representation: peerRepresentation }
        setWorkingRepresentation(parsedRepresentation)
        setError(null)
        // Mark as search mode
        setIsSearchMode(true)
        setCurrentSearchQuery(inputText.trim())
      } else {
        setError('No contextual representation found')
      }
    } catch (err) {
      console.error('Error fetching contextual representation:', err)
      setError('Failed to fetch contextual representation')
    } finally {
      setIsSearchLoading(false)
    }
  }, [currentSession.id, user?.id, clearVizState])

  const handleContextSearch = () => {
    if (searchText.trim()) {
      fetchContextualRepresentation(searchText)
    }
  }


  useEffect(() => {
    // Don't fetch data if user is not authenticated
    if (!user) {
      setIsLoading(false)
      return
    }

    // Initial load - show loading screen
    fetchWorkingRepresentation(true)
  }, [user, fetchWorkingRepresentation])

  // Listen for refresh events from authenticated chat turns
  useEffect(() => {
    if (!user) return

    const handleRefreshEvent = () => {
      // Background update - don't show loading screen
      fetchWorkingRepresentation(false)
    }

    window.addEventListener('working-representation-refresh', handleRefreshEvent)

    return () => {
      window.removeEventListener('working-representation-refresh', handleRefreshEvent)
    }
  }, [user, fetchWorkingRepresentation])

  const handleRefresh = () => {
    // Clear visualization state before refreshing to full representation
    clearVizState()
    // Exit search mode
    setIsSearchMode(false)
    setCurrentSearchQuery('')
    setSearchText('')
    // Manual refresh - show loading briefly
    fetchWorkingRepresentation(true)
  }

  // Handle window resize to recalculate canvas dimensions
  // Only set up after data is loaded and container is rendered
  useEffect(() => {
    if (!containerRef.current || isLoading || !workingRepresentation) {
      return
    }

    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        if (rect.width > 0 && rect.height > 0) {
          setContainerSize((prev) => {
            if (prev.width !== rect.width || prev.height !== rect.height) {
              return { width: rect.width, height: rect.height }
            }
            return prev
          })
        }
      }
    }

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect
        // Ignore zero-size updates (can happen during DOM updates)
        if (width === 0 || height === 0) {
          continue
        }
        // Only update if size actually changed (avoids unnecessary re-renders)
        setContainerSize((prev) => {
          if (prev.width !== width || prev.height !== height) {
            return { width, height }
          }
          return prev
        })
      }
    })

    resizeObserver.observe(containerRef.current)

    // Set initial size immediately and after animation frame
    updateSize()
    requestAnimationFrame(updateSize)
    // Also measure after a short delay to ensure DOM is fully laid out
    setTimeout(updateSize, 100)

    return () => {
      resizeObserver.disconnect()
    }
  }, [isLoading, workingRepresentation])

  // Don't render if user is not authenticated
  if (isUserLoading || !user) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          padding: theme.spacing.lg,
          gap: theme.spacing.md,
        }}
      >
        <div style={{ fontSize: '32px', fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔐')}</div>
        <div
          style={{
            color: colors.primaryText,
            fontSize: isMobile ? '14px' : theme.fontSize.md,
            fontWeight: 600,
            textAlign: 'center',
          }}
        >
          Authentication Required
        </div>
        <div
          style={{
            color: colors.secondaryText,
            fontSize: isMobile ? '12px' : theme.fontSize.sm,
            textAlign: 'center',
            maxWidth: '280px',
          }}
        >
          Please log in to view your working representation
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: theme.spacing.md,
        }}
      >
        <div
          style={{
            fontSize: theme.fontSize.sm,
            color: colors.errorText || colors.secondaryText,
            textAlign: 'center',
          }}
        >
          {error}
        </div>
        <button
          onClick={handleRefresh}
          style={{
            backgroundColor: colors.primaryAccent,
            color: colors.primaryBg,
            border: 'none',
            borderRadius: theme.borderRadius.sm,
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            fontSize: theme.fontSize.sm,
            cursor: 'pointer',
          }}
        >
          Retry
        </button>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <LoadingText
          {...loadingPresets.thinking('Loading representation...', colors.primaryAccent || colors.primaryText)}
          fontSize={isMobile ? '12px' : '14px'}
        />
      </div>
    )
  }

  return (
    <div
      style={{
        position: 'relative',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        backgroundColor: colors.primaryBg,
      }}
    >
      {/* Search Controls */}
      <div
        style={{
          position: 'absolute',
          top: theme.spacing.md,
          left: theme.spacing.md,
          right: theme.spacing.md,
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.xs,
        }}
      >
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? '8px' : theme.spacing.sm,
        }}>
          {/* Search input - takes remaining space */}
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !isSearchLoading) {
                handleContextSearch()
              }
            }}
            placeholder={isMobile ? "search context..." : "Search for contextual representation..."}
            disabled={isSearchLoading}
            style={{
              flex: 1,
              minWidth: 0,
              backgroundColor: colors.secondaryBg,
              color: colors.primaryText,
              border: `1px solid ${colors.border}`,
              borderRadius: theme.borderRadius.sm,
              padding: isMobile ? '10px 12px' : `${theme.spacing.xs} ${theme.spacing.sm}`,
              fontSize: isMobile ? '12px' : theme.fontSize.sm,
              outline: 'none',
              opacity: isSearchLoading ? 0.5 : 1,
              height: isMobile ? '36px' : 'auto',
            }}
          />
          
          {/* Action buttons - fixed size, consistent styling */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: isMobile ? '6px' : theme.spacing.xs,
            flexShrink: 0,
          }}>
            <button
              onClick={handleContextSearch}
              disabled={isSearchLoading || !searchText.trim()}
              style={{
                backgroundColor: colors.primaryAccent,
                color: colors.primaryBg,
                border: 'none',
                borderRadius: theme.borderRadius.sm,
                cursor: (isSearchLoading || !searchText.trim()) ? 'not-allowed' : 'pointer',
                opacity: (isSearchLoading || !searchText.trim()) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(isMobile ? {
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  fontSize: '16px',
                } : {
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  fontSize: theme.fontSize.sm,
                }),
              }}
              title="Search for contextual representation"
            >
              {isMobile ? (isSearchLoading ? '...' : <span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔍')}</span>) : (isSearchLoading ? 'Searching...' : 'Search')}
            </button>
            <button
              onClick={handleRefresh}
              disabled={isLoading || isSearchLoading}
              style={{
                backgroundColor: colors.primaryAccent,
                color: colors.primaryBg,
                border: 'none',
                borderRadius: theme.borderRadius.sm,
                cursor: (isLoading || isSearchLoading) ? 'not-allowed' : 'pointer',
                opacity: (isLoading || isSearchLoading) ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(isMobile ? {
                  width: '36px',
                  height: '36px',
                  padding: 0,
                  fontSize: '22px',
                } : {
                  padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                  fontSize: theme.fontSize.sm,
                }),
              }}
              title="Refresh to original working representation"
            >
              {isMobile ? '↻' : 'Refresh'}
            </button>
            <RepresentationHelpToggle />
          </div>
        </div>
        {/* Search Mode Indicator */}
        {isSearchMode && currentSearchQuery && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing.xs,
              padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
              backgroundColor: colors.primaryAccent,
              color: colors.primaryBg,
              borderRadius: theme.borderRadius.sm,
              fontSize: theme.fontSize.xs,
              fontWeight: 500,
            }}
          >
            <span><span style={{ fontFamily: "'Noto Emoji', sans-serif" }}>{normalizeEmojis('🔍')}</span> Filtered by: "{currentSearchQuery}"</span>
            <button
              onClick={handleRefresh}
              style={{
                background: 'none',
                border: 'none',
                color: colors.primaryBg,
                cursor: 'pointer',
                padding: '0 4px',
                fontSize: theme.fontSize.md,
                lineHeight: 1,
              }}
              title="Clear filter"
            >
              ×
            </button>
          </div>
        )}
      </div>

      {/* Help Banner - Positioned absolutely to overlay */}
      {helpEnabled && (
        <div
          style={{
            position: 'absolute',
            top: isSearchMode ? `calc(${theme.spacing.md} + 68px)` : `calc(${theme.spacing.md} + 40px)`,
            left: isMobile ? '8px' : theme.spacing.md,
            right: isMobile ? '8px' : theme.spacing.md,
            zIndex: 20,
            padding: isMobile ? '8px 12px' : `${theme.spacing.sm} ${theme.spacing.md}`,
            backgroundColor: colors.primaryBg,
            border: `1px solid ${colors.primaryBorder}`,
            borderRadius: theme.borderRadius.sm,
            color: colors.secondaryText,
            fontSize: isMobile ? '11px' : theme.fontSize.sm,
            textAlign: 'center',
            lineHeight: '1.6',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          }}
        >
          <span style={{ color: colors.primaryText, fontWeight: 600 }}>
            📊 Your Representation
          </span>
          {' • '}
          {t('help.representation.banner')}
        </div>
      )}

      {/* ASCII Visualization Container */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: isSearchMode ? `calc(${theme.spacing.md} + 68px)` : `calc(${theme.spacing.md} + 40px)`,
          left: 0,
          right: 0,
          bottom: 0,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {containerSize.width > 0 && containerSize.height > 0 && (
          <WorkingRepresentationAsciiViz
            key={isSearchMode ? `search-${currentSearchQuery}` : 'full-representation'}
            workingRepresentation={workingRepresentation}
            userId={user?.id || ''}
            width={containerSize.width}
            height={containerSize.height}
            colors={colors}
            growthSpeed={isMobile ? 'fast' : 'medium'}
            charSize={isMobile ? 14 : 10}
            helpEnabled={helpEnabled}
            isActive={isActive}
          />
        )}
      </div>
    </div>
  )
}

interface RepresentationProps {
  isActive?: boolean
}

export default function Representation({ isActive = true }: RepresentationProps) {
  return (
    <RepresentationHelpProvider>
      <RepresentationContent isActive={isActive} />
    </RepresentationHelpProvider>
  )
}
